/**
 * Driver Carrier Performance (V1 parity) — load-completion rating prompt.
 *
 * After a load's status changes (any grid / surface that uses the status-change
 * mutations), the client asks the server whether this load should prompt for
 * driver/carrier ratings. If so, a global modal (LoadRatingPromptModal) opens.
 * The server decides (feature on + Completed status + active questions + a
 * ratable assigned driver), so the client stays dumb and works everywhere.
 */
import { create } from 'zustand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface RatingPromptDriver {
  id: string;
  name: string | null;
  driverType: string;
}

export interface LoadRatingPrompt {
  loadId: string;
  displayId: string | null;
  drivers: RatingPromptDriver[];
  questions: Array<{ id: string; question: string }>;
}

export interface AutoBlacklistedDriver {
  id: string;
  name: string | null;
  rating: number | null;
}

interface PromptStore {
  prompt: LoadRatingPrompt | null;
  open: (p: LoadRatingPrompt) => void;
  close: () => void;
}

export const useLoadRatingPromptStore = create<PromptStore>((set) => ({
  prompt: null,
  open: (p) => set({ prompt: p }),
  close: () => set({ prompt: null }),
}));

/**
 * Best-effort: ask the server whether to prompt for ratings on this load and,
 * if so, open the global modal. Called from the status-change mutations'
 * onSuccess. Never throws — a failed prompt fetch just means no dialog.
 */
export async function maybeOpenLoadRatingPrompt(loadId: string): Promise<void> {
  try {
    const res = await api.get<{ data: LoadRatingPrompt | null }>(`/loads/${loadId}/rating-prompt`);
    const prompt = res.data.data;
    if (prompt && prompt.drivers.length > 0 && prompt.questions.length > 0) {
      useLoadRatingPromptStore.getState().open(prompt);
    }
  } catch {
    /* non-fatal — the rating prompt is an optional convenience */
  }
}

export function useSubmitLoadDriverRatings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      loadId: string;
      driverId: string;
      ratings: Array<{ questionId: string; rating: number }>;
      notes?: string;
    }) => {
      const { loadId, ...body } = vars;
      const res = await api.post<{
        data: { created: number; autoBlacklisted: AutoBlacklistedDriver[]; warned: AutoBlacklistedDriver[] };
      }>(`/loads/${loadId}/driver-ratings`, body);
      return res.data.data;
    },
    onSuccess: () => {
      // Refresh the Fleet grid so the recomputed rating / blacklist colour shows.
      qc.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}
